import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  FaStar,
  FaGem,
  FaBullseye,
  FaGlobe,
  FaHeart,
  FaHandshake,
  FaMagic,
  FaPalette,
  FaHome,
  FaChevronRight,
  FaHotel,
  FaBuilding,
  FaBed,
  FaGlobeAmericas,
  FaTrophy,
  FaPlay,
  FaQuoteLeft,
  FaArrowRight,
  FaMapMarkerAlt,
} from "react-icons/fa";
import "../Vender/css/Aboutus.css";

const Aboutus = () => {
  const path = useLocation().pathname;
  const [countersVisible, setCountersVisible] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const counterRef = useRef(null);
  const videoRef = useRef(null);

  // Counter animation
  const useCounter = (end, duration = 2000, start = 0, isVisible) => {
    const [count, setCount] = useState(start);

    useEffect(() => {
      if (!isVisible) return;

      let startTime = null;
      const animate = (currentTime) => {
        if (!startTime) startTime = currentTime;
        const progress = Math.min((currentTime - startTime) / duration, 1);
        setCount(Math.floor(progress * (end - start) + start));
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      requestAnimationFrame(animate);
    }, [end, duration, start, isVisible]);

    return count;
  };

  // Intersection Observer for counter animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setCountersVisible(true);
        }
      },
      { threshold: 0.3 },
    );

    if (counterRef.current) {
      observer.observe(counterRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Auto rotate features
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % 4);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const hotelBrands = useCounter(45, 2000, 0, countersVisible);
  const hotels = useCounter(5600, 2000, 0, countersVisible);
  const rooms = useCounter(850000, 2500, 0, countersVisible);
  const countries = useCounter(100, 2000, 0, countersVisible);

  const features = [
    {
      icon: <FaStar />,
      title: "Premium Experience",
      description: "Handpicked luxury destinations for unforgettable memories",
      color: "#FF6B6B",
    },
    {
      icon: <FaGem />,
      title: "Best Value",
      description:
        "Exclusive luxury holidays at surprisingly affordable prices",
      color: "#4ECDC4",
    },
    {
      icon: <FaBullseye />,
      title: "Personalized Service",
      description: "Tailored experiences designed around your preferences",
      color: "#45B7D1",
    },
    {
      icon: <FaGlobe />,
      title: "Global Access",
      description: "450+ 4-5 star hotels and resorts worldwide",
      color: "#96CEB4",
    },
  ];

  const teamValues = [
    {
      icon: <FaHeart />,
      title: "Passion",
      text: "We love what we do",
      gradient: "linear-gradient(135deg, #FF6B6B 0%, #FF8E8E 100%)",
    },
    {
      icon: <FaHandshake />,
      title: "Trust",
      text: "Building lasting relationships",
      gradient: "linear-gradient(135deg, #4ECDC4 0%, #6EE7DF 100%)",
    },
    {
      icon: <FaMagic />,
      title: "Excellence",
      text: "Exceeding expectations always",
      gradient: "linear-gradient(135deg, #A855F7 0%, #C084FC 100%)",
    },
    {
      icon: <FaPalette />,
      title: "Creativity",
      text: "Crafting unique experiences",
      gradient: "linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)",
    },
  ];

  const stats = [
    {
      icon: <FaHotel />,
      value: hotelBrands,
      suffix: "+",
      label: "Hotel Brands",
    },
    { icon: <FaBuilding />, value: hotels, suffix: "+", label: "Hotels" },
    { icon: <FaBed />, value: rooms, suffix: "+", label: "Rooms" },
    {
      icon: <FaGlobeAmericas />,
      value: countries,
      suffix: "+",
      label: "Countries",
    },
  ];

  const handleVideoPlay = () => {
    if (videoRef.current) {
      if (isVideoPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsVideoPlaying(!isVideoPlaying);
    }
  };

  return (
    <section id="aboutus-elegant" className="aboutus-elegant">
      {/* Cinematic Hero Section */}
      <header className="about-hero-elegant">
        <div className="hero-media">
          <div
            className="hero-image"
            style={{ backgroundImage: "url(/assets/images/travel.jpg)" }}
          ></div>
          <div className="hero-gradient-overlay"></div>
          <div className="hero-noise"></div>
        </div>

        {/* Floating Elements */}
        <div className="hero-floating-elements">
          <div className="floating-circle circle-1"></div>
          <div className="floating-circle circle-2"></div>
          <div className="floating-circle circle-3"></div>
          <div className="floating-line line-1"></div>
          <div className="floating-line line-2"></div>
        </div>

        <div className="hero-container">
          {/* Breadcrumb */}
          <nav className="breadcrumb-elegant" aria-label="breadcrumb">
            <Link to="/" className="breadcrumb-link">
              <FaHome />
              <span>Home</span>
            </Link>
            <FaChevronRight className="breadcrumb-separator" />
            <span className="breadcrumb-current">
              {path === "/about" ? "About Us" : ""}
            </span>
          </nav>

          {/* Hero Content */}
          <div className="hero-content-elegant">
            {/* <div className="hero-badge-elegant">
              <span className="badge-dot"></span>
              <span>Since 2009</span>
            </div> */}

            <h1 className="hero-headline-elegant">
              <span className="headline-line">
                <span className="word">Creating</span>
              </span>
              <span className="headline-line">
                <span className="word highlight-word">Magical</span>
                <span className="word">Holiday</span>
              </span>
              <span className="headline-line">
                <span className="word highlight-word">Experiences</span>
              </span>
            </h1>

            <p className="hero-tagline-elegant">
              Where every journey becomes an unforgettable adventure
            </p>
          </div>

          {/* Hero Stats Preview */}
          <div className="hero-stats-preview">
            <div className="stat-preview-item">
              <span className="stat-value">10+</span>
              <span className="stat-label">Years</span>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-preview-item">
              <span className="stat-value">450+</span>
              <span className="stat-label">Partners</span>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-preview-item">
              <span className="stat-value">100+</span>
              <span className="stat-label">Countries</span>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="scroll-indicator-elegant">
          <div className="scroll-line">
            <div className="scroll-dot"></div>
          </div>
          <span>Scroll</span>
        </div>
      </header>
      {/* Story Section */}
      <section id="about-story" className="story-section">
        <div className="story-container">
          <div className="story-grid">
            {/* Image Column */}
            <div className="story-images">
              <div className="image-stack">
                <div className="stack-image primary-image">
                  <img
                    src="/assets/images/aboutus-content_img.jpg"
                    alt="About Aacore Holidays"
                  />
                  <div className="image-frame"></div>
                </div>
                <div className="stack-image secondary-image">
                  <img
                    src="/assets/images/travel.jpg"
                    alt="Travel Experience"
                  />
                </div>
              </div>

              {/* Experience Card */}
              <div className="experience-card-elegant">
                <div className="exp-icon">
                  <FaTrophy />
                </div>
                <div className="exp-content">
                  <span className="exp-number">10+</span>
                  <span className="exp-text">Years of Excellence</span>
                </div>
              </div>

              {/* Destinations Card */}
              <div className="destinations-card">
                <div className="dest-header">
                  <FaMapMarkerAlt />
                  <span>450+ Destinations</span>
                </div>
                <span className="dest-label">Happy Travelers</span>
              </div>
            </div>

            {/* Content Column */}
            <div className="story-content">
              <div className="content-header">
                {/* <span className="section-label">Our Story</span> */}
                <h2 className="section-heading">
                  Welcome To{" "}
                  <span className="text-gradient">Aacore Holidays</span> &
                  Resort
                </h2>
                <div className="heading-decoration">
                  <span className="deco-line"></span>
                  <span className="deco-diamond"></span>
                  <span className="deco-line"></span>
                </div>
              </div>

              <div className="content-body">
                <p className="lead-paragraph">
                  Aacore Holidays Resort is synonymous with creating a Magical
                  experience for your exclusively well-planned holidays.
                </p>
                <p>
                  We provide you with an exclusive luxury holiday at
                  surprisingly low prices. With the most intimate experiences,
                  personalized residences, dining, and enchanting surroundings,
                  we aim to bring you the greatest joy without stress.
                </p>
                <p>
                  Our destinations give you access to a place for all ages—be it
                  families with children, couples at all stages of life,
                  families in need for reunification, or newlyweds seeking the
                  perfect getaway.
                </p>
              </div>

              {/* Quote */}
              <div className="story-quote">
                <FaQuoteLeft className="quote-icon" />
                <blockquote>
                  "Travel is the only thing you buy that makes you richer."
                </blockquote>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Features Section */}
      <section className="features-section-elegant">
        <div className="features-container">
          <div className="features-header">
            {/* <span className="section-label">Why Choose Us</span> */}
            <h2 className="section-heading">
              What Makes Us <span className="text-gradient">Different</span>
            </h2>
          </div>

          <div className="features-showcase">
            {/* Feature Tabs */}
            <div className="feature-tabs">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className={`feature-tab ${activeFeature === index ? "active" : ""}`}
                  onClick={() => setActiveFeature(index)}
                  style={{ "--accent-color": feature.color }}
                >
                  <div className="tab-indicator"></div>
                  <div className="tab-icon">{feature.icon}</div>
                  <div className="tab-content">
                    <h4>{feature.title}</h4>
                    <p>{feature.description}</p>
                  </div>
                  <div className="tab-arrow">
                    <FaArrowRight />
                  </div>
                </div>
              ))}
            </div>

            {/* Feature Visual */}
            <div className="feature-visual">
              <div className="visual-card">
                <div
                  className="visual-bg"
                  style={{ background: features[activeFeature].color }}
                ></div>
                <div className="visual-content">
                  <div
                    className="visual-icon"
                    style={{ color: features[activeFeature].color }}
                  >
                    {features[activeFeature].icon}
                  </div>
                  <h3>{features[activeFeature].title}</h3>
                  <p>{features[activeFeature].description}</p>
                </div>
                <div className="visual-number">0{activeFeature + 1}</div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Stats Section */}
      <section className="stats-section-elegant" ref={counterRef}>
        <div className="stats-background">
          <div className="stats-pattern"></div>
          <div className="stats-gradient"></div>
        </div>

        <div className="stats-container">
          <div className="stats-header">
            {/* <span className="section-label light">Our Achievements</span> */}
            <h2 className="section-heading light">
              Numbers That <span className="text-gradient-light">Speak</span>
            </h2>
          </div>

          <div className="stats-grid-elegant">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="stat-card-elegant"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="stat-icon-wrapper">{stat.icon}</div>
                <div className="stat-value-wrapper">
                  <span className="stat-number">
                    {typeof stat.value === "number"
                      ? stat.value.toLocaleString()
                      : stat.value}
                  </span>
                  <span className="stat-suffix">{stat.suffix}</span>
                </div>
                <span className="stat-label-elegant">{stat.label}</span>
                <div className="stat-glow"></div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Values Section */}
      <section className="values-section-elegant">
        <div className="values-container">
          <div className="values-header">
            {/* <span className="section-label">What Drives Us</span> */}
            <h2 className="section-heading">
              Our Core <span className="text-gradient">Values</span>
            </h2>
            <p className="section-description">
              The principles that guide everything we do
            </p>
          </div>

          <div className="values-grid-elegant">
            {teamValues.map((value, index) => (
              <div
                key={index}
                className="value-card-elegant"
                style={{ "--card-gradient": value.gradient }}
              >
                <div className="value-card-inner">
                  <div className="value-icon-elegant">{value.icon}</div>
                  <h4 className="value-title">{value.title}</h4>
                  <p className="value-text">{value.text}</p>
                  <div className="value-decoration">
                    <span className="deco-number">0{index + 1}</span>
                  </div>
                </div>
                <div className="value-hover-bg"></div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Video Section */}
      <section className="video-section-elegant">
        <div className="video-container-elegant">
          <div className="video-header">
            {/* <span className="section-label">Watch Our Story</span> */}
            <h2 className="section-heading">
              Experience the <span className="text-gradient">Magic</span>
            </h2>
            <p className="section-description">
              Take a glimpse into the world of Aacore Holidays
            </p>
          </div>

          <div className="video-player-wrapper">
            <div className="video-frame">
              <video
                ref={videoRef}
                controls
                loop
                muted
                playsInline
                className="video-player"
              >
                <source
                  src="/assets/video/about-company-video1.mp4"
                  type="video/mp4"
                />
              </video>
              <div className="video-overlay-elegant">
                <button
                  className={`play-button-elegant ${isVideoPlaying ? "playing" : ""}`}
                  onClick={handleVideoPlay}
                >
                  <span className="play-ripple"></span>
                  <span className="play-ripple delay"></span>
                  <FaPlay className="play-icon-inner" />
                </button>
              </div>
            </div>

            {/* Video Decorations */}
            <div className="video-decorations-elegant">
              <div className="video-deco deco-1"></div>
              <div className="video-deco deco-2"></div>
              <div className="video-deco deco-3"></div>
              <div className="video-deco deco-4"></div>
            </div>
          </div>
        </div>
      </section>
    </section>
  );
};

export default Aboutus;
